import errorMessages from "../config/errorMessages";

/* @ngInject */
function QuoteFactory($http, QuoteVersion, $rootScope, Contact) {
  class Quote {
    constructor(data) {
      this.description = null;
      this.code = null;
      this.lastVersion = null;
      this.olderVersions = [];
      this.contact = null;
      this.quoteContacts = [];
      this.language = null;
      this.archived = null;
      this.shared = null;
      this.emailToCustomerOnAccept = null;
      this.pdfDownloadable = null;
      this.approveFromPublicView = null;
      this.token = null;
      this.publicViews = null;
      this.createdAt = null;
      this.updatedAt = null;
      this.organization = null;
      this.client = null;

      if (data) {
        this.setData(data);
      } else {
        this.archived = false;
        this.shared = false;
        this.emailToCustomerOnAccept =
          $rootScope.organization.settings.emailToCustomerOnAccept;
        this.pdfDownloadable = $rootScope.organization.settings.pdfDownloadable;
        this.approveFromPublicView =
          $rootScope.organization.settings.approveFromPublicView;
        this.publicViews = 0;
        this.language = $rootScope.organization.settings.quoteLanguage;
        this.lastVersion = new QuoteVersion();
      }
    }

    static getResourceUrl() {
      if (!$rootScope.organization)
        throw new Error("Organization was not loaded yet.");
      return `${process.env.SERVER_URL}/api/secure/organization/${$rootScope.organization.slug}/quotes`;
    }

    static get(quoteId) {
      return $http
        .get(`${Quote.getResourceUrl()}/${quoteId}`)
        .then((response) => new Quote(response.data));
    }

    static query(params) {
      return $http
        .get(Quote.getResourceUrl(), { params })
        .then((response) =>
          response.data.map((quoteData) => new Quote(quoteData))
        );
    }

    static getCount(params) {
      return $http
        .get(`${Quote.getResourceUrl()}/count`, { params })
        .then((response) => response.data);
    }

    static getLogs(quoteId) {
      return $http
        .get(`${Quote.getResourceUrl()}/${quoteId}/logs`)
        .then((response) => response.data);
    }

    static generatePdf(quoteId, quoteVersion) {
      return $http
        .get(`${Quote.getResourceUrl()}/${quoteId}/version/${quoteVersion}/pdf`)
        .then((response) => response.data);
    }

    static generatePdfWithContactSignature(quoteId, quoteVersion, contactId) {
      return $http
        .get(
          `${Quote.getResourceUrl()}/${quoteId}/version/${quoteVersion}/pdf/contact/${contactId}`
        )
        .then((response) => response.data);
    }

    static checkPdfStatus(params) {
      return $http
        .get(`${process.env.SERVER_URL}/api/public/quote/pdf-status`, {
          params,
        })
        .then((response) => response.data);
    }

    isNew() {
      return !this._id;
    }

    isLastVersion(version) {
      return this.lastVersion._id === version._id;
    }

    hasVersion(versionNumber) {
      versionNumber = parseInt(versionNumber, 10);

      if (this.lastVersion.number === versionNumber) return true;

      return this.olderVersions.some(
        (version) => version.number === versionNumber
      );
    }

    getVersion(versionNumber) {
      versionNumber = parseInt(versionNumber, 10);

      if (this.lastVersion.number === versionNumber) return this.lastVersion;

      return (
        this.olderVersions.find(
          (version) => version.number === versionNumber
        ) || this.lastVersion
      );
    }

    isShared() {
      return this.shared;
    }

    isApproved() {
      return this.lastVersion.isApproved();
    }

    isRejected() {
      return this.lastVersion.isRejected();
    }
    create(
      quoteTemplateId,
      isDefaultTemplate,
      isImportedTemplate,
      quoteToClone
    ) {
      if (!this.isNew()) throw new Error("The quote is already created");

      return $http({
        url: Quote.getResourceUrl(),
        method: "POST",
        params: {
          template: quoteTemplateId,
          isDefaultTemplate: isDefaultTemplate,
          isImportedTemplate: isImportedTemplate,
          quoteToClone: quoteToClone,
        },
        data: this,
      }).then((response) => new Quote(response.data));
    }

    createVersion(date, expirationDate) {
      if (this.isNew())
        throw new Error(
          "Can not create a new version if the quote has not been created"
        );

      return $http
        .post(`${Quote.getResourceUrl()}/${this._id}/version`, {
          date,
          expirationDate,
        })
        .then((response) => new Quote(response.data));
    }

    updateHeader() {
      return this._update({
        description: this.description,
        client: this.client?.isProspective ? this.client : undefined,
        contact: this.contact,
        quoteContacts: this.quoteContacts,
        language: this.language,
        emailToCustomerOnAccept: this.emailToCustomerOnAccept,
        pdfDownloadable: this.pdfDownloadable,
        approveFromPublicView: this.approveFromPublicView,
        lastVersion: {
          date: this.lastVersion.date,
          expirationDate: this.lastVersion.expirationDate,
          currency: this.lastVersion.currency,
          exchangeInfo: this.lastVersion.exchangeInfo,
          signature: this.lastVersion.signature,
        },
      });
    }

    updateSections() {
      return this._update({
        lastVersion: { sections: this.lastVersion.sections },
      });
    }

    changeStatusToApproved() {
      return this._changeStatusTo("approved");
    }

    changeStatusToRejected() {
      return this._changeStatusTo("rejected");
    }

    _changeStatusTo(status) {
      if (this.lastVersion.status === status)
        throw new Error("The quote is already in this status: " + status);

      return $http
        .put(`${Quote.getResourceUrl()}/${this._id}/lastVersion/status`, {
          status,
        })
        .then((response) => {
          this.updatedAt = response.data.updatedAt;
          this.lastVersion.status = status;
          return this;
        });
    }

    share() {
      if (this.archived) throw new Error("The quote is already shared");

      return $http
        .put(`${Quote.getResourceUrl()}/${this._id}/share`, { shared: true })
        .then((response) => {
          this.updatedAt = response.data.updatedAt;
          this.shared = response.data.shared;
          return this;
        });
    }

    unshare() {
      if (this.archived) throw new Error("The quote is already unshared");

      return $http
        .put(`${Quote.getResourceUrl()}/${this._id}/share`, { shared: false })
        .then((response) => {
          this.updatedAt = response.data.updatedAt;
          this.shared = response.data.shared;
          return this;
        });
    }

    archive() {
      if (this.archived) throw new Error("The quote is already archived");

      return $http
        .put(`${Quote.getResourceUrl()}/${this._id}/archive`, {
          archived: true,
        })
        .then((response) => {
          this.updatedAt = response.data.updatedAt;
          this.archived = response.data.archived;
          return this;
        });
    }

    unarchive() {
      if (!this.archived) throw new Error("The quote is already active");

      return $http
        .put(`${Quote.getResourceUrl()}/${this._id}/archive`, {
          archived: false,
        })
        .then((response) => {
          this.updatedAt = response.data.updatedAt;
          this.archived = response.data.archived;
          return this;
        });
    }

    delete() {
      if (this.isNew())
        throw new Error("Can not delete a quote that has not been created");

      return $http
        .delete(`${Quote.getResourceUrl()}/${this._id}`)
        .then(() => true);
    }

    recordEditingTime(params) {
      if (this.isNew())
        throw new Error(
          "Can not track editing time a quote that has not been created"
        );

      return $http
        .put(
          `${Quote.getResourceUrl()}/${this._id}/record-editing-time`,
          params
        )
        .then(() => true);
    }

    setData(data) {
      this._id = data._id ? data._id : undefined;
      this.description = data.description;
      this.code = data.code;
      this.archived = data.archived;
      this.shared = data.shared;
      this.emailToCustomerOnAccept = data.emailToCustomerOnAccept;
      this.pdfDownloadable = data.pdfDownloadable;
      this.approveFromPublicView = data.approveFromPublicView;
      this.token = data.token;
      this.language = data.language;
      this.publicViews = data.publicViews;
      this.createdAt = data.createdAt;
      this.updatedAt = data.updatedAt;
      this.organization = data.organization;
      this.client = data.client;

      if (data.contact) {
        if (angular.isString(data.contact)) this.contact = data.contact;
        else this.contact = new Contact(data.contact);
      }

      if (data.quoteContacts && data.quoteContacts.length) {
        this.quoteContacts = data.quoteContacts.map((quoteContact) => ({
          role: quoteContact.role,
          contact: angular.isString(quoteContact.contact)
            ? quoteContact.contact
            : new Contact(quoteContact.contact),
        }));
      }

      if (this.lastVersion) this.lastVersion.setData(data.lastVersion);
      else this.lastVersion = new QuoteVersion(data.lastVersion);

      this._updateOlderVersions(data.olderVersions);
    }

    _updateOlderVersions(olderVersionsData) {
      if (!Array.isArray(olderVersionsData)) return;

      for (let i = 0; i < olderVersionsData.length; i++) {
        if (this.olderVersions[i])
          this.olderVersions[i].setData(olderVersionsData[i]);
        else this.olderVersions.push(new QuoteVersion(olderVersionsData[i]));
      }

      if (this.olderVersions.length > olderVersionsData.length) {
        this.olderVersions = this.olderVersions.slice(
          0,
          olderVersionsData.length
        );
      }
    }

    _update(data) {
      if (this.isNew())
        throw new Error("You cannot update a quote that has not been created");

      return $http
        .put(`${Quote.getResourceUrl()}/${this._id}`, data)
        .then((response) => {
          this.updatedAt = response.data.updatedAt;
          return this;
        });
    }

    updateClientAndContacts() {
      if (this.isNew())
        throw new Error(errorMessages.unableToUpdateClientAndContacts);

      return $http
        .put(`${Quote.getResourceUrl()}/${this._id}/sync-client-and-contacts`)
        .then((response) => new Quote(response.data));
    }
  }

  return Quote;
}

export default QuoteFactory;
